import type { IDXFRenderContext, IDXFTextInfo } from '../types';
import type { BoundingBox } from '@/editor/utils/dxf/types';

export const DXF_BASEMAP_CONTEXT_VERSION = 1;

export type DxfBasemapCoordinateSpace = 'editor-mm-centered-v1';

export type DxfBasemapLayerRef = string | null;

export type DxfBasemapMatrix = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];

export interface DxfBasemapLayer {
  name: string;
  visible: boolean;
  color: number;
  lineTypeName?: string;
  lineWeightEnum?: number;
  flags: string[];
}

export interface DxfBasemapLineEntity {
  type: 'LINE';
  layer: DxfBasemapLayerRef;
  points: [number, number, number, number];
}

export interface DxfBasemapTextEntity {
  type: 'TEXT';
  layer: DxfBasemapLayerRef;
  text: string;
  position: {
    x: number;
    y: number;
  };
  rotation: number;
  size: number;
  lineHeight: number;
  align: CanvasTextAlign;
  baseline: CanvasTextBaseline;
}

export interface DxfBasemapInsertEntity {
  type: 'INSERT';
  layer: DxfBasemapLayerRef;
  blockName: string;
  matrix: DxfBasemapMatrix;
}

export type DxfBasemapEntity = DxfBasemapLineEntity | DxfBasemapTextEntity | DxfBasemapInsertEntity;

export interface DxfBasemapBlock {
  name: string;
  entities: DxfBasemapEntity[];
}

export interface DxfBasemapContextStats {
  blockCount: number;
  usedBlockCount: number;
  rootEntityCount: number;
  storedEntityCount: number;
  storedLineCount: number;
  storedTextCount: number;
  storedInsertCount: number;
  expandedLineCount: number;
  expandedTextCount: number;
}

export interface DxfBasemapContextGeneratorOptions {
  /**
   * 将 DXF 的 OCS 坐标转换为 WCS 坐标，保持与运行时解析器一致。
   * 仅在需要保留未做 OCS 归一化的历史解析结果时关闭。
   */
  transformOcs?: boolean;
  /**
   * 是否将 SOLID 实体转换为四条边线参与底图渲染。
   */
  renderSolid?: boolean;
}

export interface DxfBasemapContext {
  version: typeof DXF_BASEMAP_CONTEXT_VERSION;
  coordinateSpace: DxfBasemapCoordinateSpace;
  bounding: BoundingBox;
  /**
   * 可选的边界裁剪
   */
  lineClipBounds?: BoundingBox;
  layers: Record<string, DxfBasemapLayer>;
  entities: DxfBasemapEntity[];
  blocks: Record<string, DxfBasemapBlock>;
  usedBlockNames: string[];
  metadata: {
    unitRatio: number;
    sourceUnit?: number;
    transformOcs: boolean;
    renderSolid?: boolean;
    createdAt: string;
  };
  stats: DxfBasemapContextStats;
}

export interface DxfRenderContextBuildOptions {
  quadTreePadding?: number;
}

export interface DxfTransferableRenderContext {
  context: Omit<IDXFRenderContext, 'quadTree'>;
  quadTreeBuffer: ArrayBuffer;
}

export type DxfRuntimeRenderContext = IDXFRenderContext;

export type DxfRuntimeTextInfo = IDXFTextInfo;
